import { useEffect, useState } from "react";
import { useFirestore } from "react-redux-firebase";
import { FIRESTORE_PURCHASE_ORDER_NOTIFICATIONS_TABLE } from "../../constants/FirestoreConstant";

/**
 * Hook to fetch and manage purchase order notifications from Firestore
 * @param {Object} options
 * @param {boolean} options.onlyUnresolved - Only fetch unresolved notifications (default: true)
 * @param {boolean} options.enabled - Set to false to skip the listener entirely (e.g. user without purchasing permission)
 * @returns {{ notifications: Array, loading: boolean, error: any, markResolved: Function, markRead: Function }}
 */
export const usePurchaseOrderNotifications = (options = {}) => {
  const { onlyUnresolved = true, enabled = true } = options;
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const firestore = useFirestore();

  useEffect(() => {
    if (!enabled) {
      setNotifications([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    let query = firestore.collection(FIRESTORE_PURCHASE_ORDER_NOTIFICATIONS_TABLE);

    if (onlyUnresolved) {
      query = query.where("resolvedAt", "==", null);
    }

    query = query.orderBy("createdAt", "desc").limit(50);

    const unsubscribe = query.onSnapshot(
      (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setNotifications(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("Error fetching purchase order notifications:", err);
        setError(err);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [firestore, onlyUnresolved, enabled]);

  const markResolved = async (notificationId, resolvedByEmail) => {
    try {
      await firestore.collection(FIRESTORE_PURCHASE_ORDER_NOTIFICATIONS_TABLE).doc(notificationId).update({
        resolvedAt: Math.floor(Date.now() / 1000),
        resolvedBy: resolvedByEmail,
      });
    } catch (err) {
      console.error("Error resolving notification:", err);
    }
  };

  const markRead = async (notificationId, uid) => {
    try {
      const doc = await firestore.collection(FIRESTORE_PURCHASE_ORDER_NOTIFICATIONS_TABLE).doc(notificationId).get();
      const readBy = doc.data()?.readBy || [];
      if (!readBy.includes(uid)) {
        await firestore.collection(FIRESTORE_PURCHASE_ORDER_NOTIFICATIONS_TABLE).doc(notificationId).update({
          readBy: [...readBy, uid],
        });
      }
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  return { notifications, loading, error, markResolved, markRead };
};
